import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { DashboardFilters } from '../Dashboard/DashboardFilterBar';
import { fetchQuadrantBrandBreakdown } from './operationDashboardApi';
import type { Quadrant, Severity, BrandBreakdownCell } from './types';

const CELL_FG: Record<Severity, string> = {
  green: 'var(--color-success, #16A34A)',
  orange: 'var(--color-warning, #CA8A04)',
  red: 'var(--color-danger, #DC2626)',
  unknown: 'var(--color-text-muted, #8A8A85)',
};

interface Props {
  quadrant: Quadrant;
  filters: DashboardFilters;
  enabled: boolean;
}

const CountCell: React.FC<{ cell?: BrandBreakdownCell }> = ({ cell }) => {
  if (!cell || cell.count === null) {
    return <Box component="span" sx={{ color: 'var(--color-text-muted, #8A8A85)' }}>—</Box>;
  }
  const content = (
    <Box component="span" sx={{ fontWeight: 600, color: CELL_FG[cell.severity] }}>{cell.count}</Box>
  );
  return cell.count === 0
    ? <Box component="span" sx={{ color: 'var(--color-text-muted, #8A8A85)' }}>0</Box>
    : <Link to={cell.drill_url} style={{ textDecoration: 'none' }}>{content}</Link>;
};

const BrandBreakdownMatrix: React.FC<Props> = ({ quadrant, filters, enabled }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['op-dash-breakdown', quadrant, filters],
    queryFn: () => fetchQuadrantBrandBreakdown(quadrant, filters),
    enabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 25_000,
  });

  if (isLoading) {
    return <Box sx={{ height: 80, mt: 1, borderRadius: 'var(--radius-md, 6px)', bgcolor: 'var(--color-bg-tertiary, #EDEDE8)' }} />;
  }
  if (error) {
    return <Typography sx={{ fontSize: '12px', color: 'var(--color-danger, #DC2626)', mt: 1 }}>Couldn&apos;t load brand detail — retry</Typography>;
  }
  if (!data || data.brands.length === 0) {
    return <Typography sx={{ fontSize: '12px', color: 'var(--color-text-muted)', mt: 1 }}>No brand-level data</Typography>;
  }

  const cellFor = (brand: string, ruleId: string) => data.cells[brand]?.find((c) => c.rule_id === ruleId);

  return (
    <Box sx={{ mt: 1, overflowX: 'auto' }}>
      <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: '12px' }}>
        <thead>
          <tr>
            <Box component="th" sx={{ textAlign: 'left', p: '4px 8px', color: 'var(--color-text-muted)' }}>Element</Box>
            {data.brands.map((b) => (
              <Box component="th" key={b} sx={{ textAlign: 'right', p: '4px 8px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{b}</Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rules.map((r) => (
            <tr key={r.id}>
              <Box component="td" sx={{ p: '4px 8px', color: 'var(--color-text-primary)', whiteSpace: 'nowrap' }}>{r.label}</Box>
              {data.brands.map((b) => (
                <Box component="td" key={b} sx={{ p: '4px 8px', textAlign: 'right' }}>
                  <CountCell cell={cellFor(b, r.id)} />
                </Box>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
      {data.overflow_brands > 0 && (
        <Typography sx={{ fontSize: '11px', color: 'var(--color-text-muted)', mt: 0.5 }}>
          +{data.overflow_brands} more brands — filter to narrow
        </Typography>
      )}
    </Box>
  );
};

export default BrandBreakdownMatrix;
